import { useMeetingStore } from '../../stores/meetingStore';
import { Button } from '../common/Button';
import { Tooltip } from '../common/Tooltip';
import { CameraOffIcon, CameraOnIcon, FlipCameraIcon, MicOffIcon, MicOnIcon, ScreenShareIcon } from './ControlIcons';
import { isMobileDevice } from '../../services/webrtc/mediaDevices';

interface ControlBarProps {
  onLeave?: () => void;
}

/**
 * Meeting control bar with mute, camera, screen share, and leave buttons.
 */
export function ControlBar({ onLeave }: ControlBarProps) {
  const {
    localParticipant,
    isSharingScreen,
    toggleMute,
    toggleCamera,
    flipCamera,
    startScreenShare,
    stopScreenShare,
    leaveRoom,
    cameraDevices,
  } = useMeetingStore();

  const isMuted = localParticipant?.isMuted ?? false;
  const isCameraOn = localParticipant?.isCameraOn ?? false;
  const hasMultipleCameras = cameraDevices.length > 1;
  const isMobile = isMobileDevice();
  const handleScreenShare = createScreenShareHandler(isSharingScreen, startScreenShare, stopScreenShare);
  const handleFlipCamera = () => void flipCamera();
  const handleLeave = onLeave ?? leaveRoom;

  return (
    <div className="flex items-center justify-center gap-4 px-6 py-4 bg-surface/80 backdrop-blur-lg border-t border-border flex-shrink-0">
      <ControlGroup
        isMuted={isMuted}
        isCameraOn={isCameraOn}
        isSharingScreen={isSharingScreen}
        hasMultipleCameras={hasMultipleCameras}
        isMobile={isMobile}
        onToggleMute={toggleMute}
        onToggleCamera={toggleCamera}
        onFlipCamera={handleFlipCamera}
        onToggleShare={handleScreenShare}
      />
      <LeaveButton onLeave={handleLeave} />
    </div>
  );
}

interface ControlGroupProps {
  isMuted: boolean;
  isCameraOn: boolean;
  isSharingScreen: boolean;
  hasMultipleCameras: boolean;
  isMobile: boolean;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  onFlipCamera: () => void;
  onToggleShare: () => void;
}

/**
 * Grouped meeting controls with tooltips.
 */
function ControlGroup({
  isMuted,
  isCameraOn,
  isSharingScreen,
  hasMultipleCameras,
  isMobile,
  onToggleMute,
  onToggleCamera,
  onFlipCamera,
  onToggleShare,
}: ControlGroupProps) {
  return (
    <div className="flex items-center gap-2 rounded-full bg-white/5 border border-border p-2">
      <Tooltip content={isMuted ? 'Unmute' : 'Mute'}>
        <ControlButton
          onClick={onToggleMute}
          isActive={!isMuted}
          activeIcon={<MicOnIcon />}
          inactiveIcon={<MicOffIcon />}
          label={isMuted ? 'Unmute' : 'Mute'}
        />
      </Tooltip>

      <Tooltip content={isCameraOn ? 'Stop Camera' : 'Start Video'}>
        <ControlButton
          onClick={onToggleCamera}
          isActive={isCameraOn}
          activeIcon={<CameraOnIcon />}
          inactiveIcon={<CameraOffIcon />}
          label={isCameraOn ? 'Stop Camera' : 'Start Video'}
        />
      </Tooltip>

      {isCameraOn && hasMultipleCameras && (
        <Tooltip content="Flip Camera">
          <ControlButton
            onClick={onFlipCamera}
            isActive={false}
            activeIcon={<FlipCameraIcon />}
            inactiveIcon={<FlipCameraIcon />}
            label="Flip"
          />
        </Tooltip>
      )}

      {!isMobile && (
        <Tooltip content={isSharingScreen ? 'Stop Share' : 'Share Screen'}>
          <ControlButton
            onClick={onToggleShare}
            isActive={isSharingScreen}
            activeIcon={<ScreenShareIcon />}
            inactiveIcon={<ScreenShareIcon />}
            label={isSharingScreen ? 'Stop Share' : 'Share Screen'}
          />
        </Tooltip>
      )}
    </div>
  );
}

interface LeaveButtonProps {
  onLeave: () => void;
}

/**
 * Leave meeting action button.
 */
function LeaveButton({ onLeave }: LeaveButtonProps) {
  return (
    <Button variant="danger" onClick={onLeave} className="px-6">
      Leave
    </Button>
  );
}

/**
 * Creates a handler for screen share toggling.
 */
function createScreenShareHandler(
  isSharing: boolean,
  startShare: () => Promise<void>,
  stopShare: () => void
): () => void {
  return () => {
    if (isSharing) {
      stopShare();
    } else {
      void startShare();
    }
  };
}

interface ControlButtonProps {
  onClick: () => void;
  isActive: boolean;
  activeIcon: React.ReactNode;
  inactiveIcon: React.ReactNode;
  label: string;
}

/**
 * Icon button for meeting controls.
 */
function ControlButton({
  onClick,
  isActive,
  activeIcon,
  inactiveIcon,
  label,
}: ControlButtonProps) {
  return (
    <button
      onClick={onClick}
      aria-pressed={isActive}
      className={`
        flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200
        ${isActive ? 'bg-accent/20 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'}
      `}
      title={label}
    >
      {isActive ? activeIcon : inactiveIcon}
      <span className="text-xs">{label}</span>
    </button>
  );
}
